import { PerimetersService } from '../../../@core/utils/perimeters.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, Subscription, throwError } from 'rxjs';
import {
  ConfigService,
  I18nService,
  InvestmentScenariiService,
  LayoutService,
  PerimeterStatsService,
  ReferenceDataService,
  ScopeService,
  UsersService,
  UtilsService,
  YearsService,
  SettingsService,
} from '../../../@core/utils';
import { APIService, KPI } from '../../../@core/api';
import { SiteMainInfoTabsComponent } from './site-main-info-tabs/site-main-info-tabs.component';
import { RseEnergyCarbonPrevisionComponent } from './rse-energy-carbon-prevision/rse-energy-carbon-prevision.component';
import { catchError, filter, finalize, map, retry, switchMap, take, tap } from 'rxjs/operators';
import { NbCardModule, NbDialogRef, NbDialogService, NbPopoverModule, NbSpinnerModule, NbTabComponent } from '@nebular/theme';
import { RightPanelDialogService } from '../../../@core/utils/right-panel-dialog.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AsyncPipe, NgStyle } from '@angular/common';
import { SiteOverviewReportsComponent } from './site-overview-reports/site-overview-reports.component';
import { NavigationStart, Router } from '@angular/router';
import {
  EnergySettingsDialogComponent,
  EnergySettingsType,
} from '../energy-settings-dialog/energy-settings-dialog.component';
import { PerimeterSelectorComponent } from './perimeter-selector/perimeter-selector.component';
import { InvestmentsSpreadsheetComponent } from '../investments-spreadsheet/investments-spreadsheet.component';
import { InitiativesSpreadsheetComponent } from '../initiatives-spreadsheet/initiatives-spreadsheet.component';
import { AgGridAssetComponent } from '../ag-grid-asset/ag-grid-asset.component';
import { TableFilterComponent } from '../table-filter/table-filter.component';
import { environment } from '../../../../environments/environment';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SideFormDialogComponent } from '../side-forms/side-form-dialog/side-form-dialog.component';
import { SitesSideFormComponent } from '../side-forms/site-form/site-form.component';
import {
  GROUP_PERMISSION_ACCESS_AUDIT,
  GROUP_PERMISSION_ACCESS_EQUIPMENT_VALIDATION,
  GROUP_PERMISSION_ACCESS_GENERIC_GAMME,
  GROUP_PERMISSION_EDIT_GAMME_SITE,
  GROUP_PERMISSION_EDIT_SITE,
} from '../../../@core/api/models';
import { SpreadsheetSitesService } from '../../../@core/utils/spreadsheet-sites.service';
import { SiteControlReportsComponent } from './site-control-reports/site-control-reports.component';
import { SiteObligationsReglementairesComponent } from './site-obligations-reglementaires/site-obligations-reglementaires.component';
import { CircularSpinnerComponent } from '../circular-spinner/circular-spinner.component';

export type SiteOverviewView = 'main-info' | 'obligations' | 'reports' | 'ppa' | 'energy' | 'actions' | 'assets';

@Component({
    selector: 'app-site-overview',
    templateUrl: './site-overview.component.html',
    styleUrls: ['./site-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
      
      RseEnergyCarbonPrevisionComponent,
      SiteMainInfoTabsComponent,
      TranslateModule,
      NbSpinnerModule,
      AsyncPipe,
      NbPopoverModule,
      NbCardModule,
      NgStyle,
      SiteOverviewReportsComponent,
      PerimeterSelectorComponent,
      InvestmentsSpreadsheetComponent,
      InitiativesSpreadsheetComponent,
      AgGridAssetComponent,
      TableFilterComponent,
      FontAwesomeModule,
      SiteControlReportsComponent,
      SiteObligationsReglementairesComponent,
      CircularSpinnerComponent,
    ],
})
export class SiteOverviewComponent implements OnDestroy, OnInit {

  @ViewChild(AgGridAssetComponent) grid!: AgGridAssetComponent
  @Input() public perimeter: KPI.Perimeter;
  @Input() public initialView: SiteOverviewView;

  public focusedPerimeter$: Observable<KPI.Perimeter>;
  public perimeterStats: KPI.PerimeterStatsDetails;
  public perimeterPicture: string;
  public loadingPicture: boolean = true;
  public powerpointReportLoading: boolean = false;
  public selectedView: SiteOverviewView = 'main-info';
  public focusedPerimeterId: number | null = null;
  public referenceDataLoading: boolean = false;
  public referenceDataRefreshKey: number = 0;
  public conformityManagementEnabled$: Observable<boolean>;
  public selectedTabId: number;
  public energyEnabled$: Observable<boolean> = this.scopeService.user$.pipe(map((user) => user.can_access_energy));
  public canAccessReport$: Observable<boolean> = this.scopeService.user$.pipe(map((user) => user.can_access_report));
  public canAccessObligations$: Observable<boolean> = this.usersService.hasCurrentUserAtLeastOnePermission([
    GROUP_PERMISSION_ACCESS_GENERIC_GAMME,
    GROUP_PERMISSION_ACCESS_EQUIPMENT_VALIDATION,
    GROUP_PERMISSION_EDIT_GAMME_SITE,
  ]);
  public isFidjiAct: boolean = false;
  public scenario: KPI.InvestmentScenario;
  public canAccessAudit$: Observable<boolean> = combineLatest([
    this.configService.customerConfig$,
    this.usersService.hasCurrentUserPermissions([GROUP_PERMISSION_ACCESS_AUDIT])
  ]).pipe(
    map(([config, auditAppGroupPermission]) => config.can_access_audit_app && auditAppGroupPermission)
  );
  public canEditSite$: Observable<boolean> = this.usersService.hasCurrentUserPermissions([GROUP_PERMISSION_EDIT_SITE]);
  public showInitiatives: boolean = false;
  public showTrajectories: boolean = false;
  
  private showInitiativesSubject$ = new BehaviorSubject<boolean>(false);
  private showTrajectoriesSubject$ = new BehaviorSubject<boolean>(false);
  
  private getEnergyFeatureEnabled$(showFeature$: Observable<boolean>): Observable<boolean> {
    return combineLatest([
      this.energyEnabled$,
      showFeature$
    ]).pipe(
      map(([hasPermission, showFeature]) => {
        const energyModuleEnabled = this.settingsService.settingMustBeTrue('ENERGY__ENABLE');
        return hasPermission && showFeature && energyModuleEnabled;
      })
    );
  }
  
  public showInitiatives$: Observable<boolean> = this.getEnergyFeatureEnabled$(this.showInitiativesSubject$.asObservable());
  public showTrajectories$: Observable<boolean> = this.getEnergyFeatureEnabled$(this.showTrajectoriesSubject$.asObservable());

  // Used to know what width should we apply to the dialog
  public sheetDialogNb: number = 0;
  private subscriptions: Subscription[] = [];
  public tablesFilter: any;
  public filtersNb: number = 0;
  public techStateAssets: any = {
    total: 0,
    techStateAssetsNb: 0,
  };
  public alertsData: any = null;

  constructor(
    private perimetersService: PerimetersService,
    private perimeterStatsService: PerimeterStatsService,
    private yearsService: YearsService,
    private i18n: I18nService,
    private cdRef: ChangeDetectorRef,
    private configService: ConfigService,
    private scopeService: ScopeService,
    public layoutService: LayoutService,
    private nbDialogRef: NbDialogRef<SiteOverviewComponent>,
    private router: Router,
    private dialogService: NbDialogService,
    private investmentScenariiService: InvestmentScenariiService,
    private utils: UtilsService,
    private translate: TranslateService,
    public referenceDataService: ReferenceDataService,
    private usersService: UsersService,
    private spreadsheetSitesService: SpreadsheetSitesService,
    private api: APIService,
    private settingsService: SettingsService,
    private rightPanelDialogService: RightPanelDialogService,
  ) { }

  public ngOnInit(): void {
    this.conformityManagementEnabled$ = this.settingsService.assetConformityManagement$;

    this.layoutService.addSheetDialogNb();
    this.sheetDialogNb = this.layoutService.sheetDialogNb;
    this.tablesFilter = {
      site: [this.perimeter.id],
    };

    this.focusedPerimeter$ = this.perimetersService.getSitePerimeterFocused(this.perimeter);
    if (this.initialView) {
      this.selectedView = this.initialView;
    }

    this.subscriptions.push(
      this.nbDialogRef.onClose.subscribe(() => {
        this.layoutService.removeSheetDialogNb();
      }),
      this.router.events.pipe(
        filter(event => event instanceof NavigationStart),
      ).subscribe(() => this.close()),
      this.investmentScenariiService.mainScenario$.subscribe((mainScenario) => {
        this.scenario = mainScenario;
        this.cdRef.detectChanges();
      }),
      this.canAccessObligations$.subscribe((canAccessObligations) => {
        if (!canAccessObligations && this.selectedView === 'obligations') {
          this.selectedView = 'main-info';
          this.cdRef.markForCheck();
        }
      }),

      this.perimetersService._perimeterPicturesRefresh$.pipe(
        filter(event => event.perimeterId === this.perimeter.id),
      ).subscribe(({ coverPictureUrl }) => {
        if (coverPictureUrl) {
          this.perimeterPicture = coverPictureUrl;
          this.cdRef.markForCheck();
        }
      }),

      this.perimetersService._perimeterDataRefresh$.pipe(
        filter(event => event.perimeterId === this.perimeter.id),
      ).subscribe(({ data }) => {
        if (data.name !== undefined) this.perimeter.name = data.name;
        if (data.sub_perimeters) {
          const nameMap = new Map<number, string>(
            (data.sub_perimeters as KPI.Perimeter[])
              .filter(sp => sp.name !== undefined)
              .map(sp => [sp.id, sp.name])
          );
          (this.perimeter.sub_perimeters || []).forEach(sp => {
            const newName = nameMap.get(sp.id);
            if (newName !== undefined) sp.name = newName;
          });
        }
        this.cdRef.markForCheck();
      }),

      this.yearsService.latestYears$.pipe(
        switchMap(([start_year, end_year]) => {
          return combineLatest([
            this.perimeterStatsService.perimeterStats(this.perimeter, start_year, end_year, true), // Perimeter global statistics
            this.perimetersService.getPerimeterPictures(this.perimeter), // Perimeter pictures
            this.configService.customerConfig$, // use picture in customer config if no perimeter pic
          ]);
        })
      ).subscribe(([perimeterStats, perimeterPictures, customerConfig]) => {
        const stats = perimeterStats as KPI.PerimeterStatsDetails;

        if (perimeterPictures?.length) {
          if (this.perimeter.cover_image) {
            const perimeterCoverImage = perimeterPictures.find((p) => p.id === this.perimeter.cover_image);
            if (perimeterCoverImage) {
              this.perimeterPicture = perimeterCoverImage.picture;
            } else {
              this.perimeterPicture = perimeterPictures[0].picture;
            }
          } else {
            this.perimeterPicture = perimeterPictures[0].picture;
          }
        } else if (customerConfig?.picture) {
          this.perimeterPicture = customerConfig.picture.picture
        } else {
          // Use default picture when no picture is available
          this.perimeterPicture = 'assets/images/site-default.jpg';
        }

        this.loadingPicture = false;

        this.perimeterStats = {
          ...stats,
          period_planned_budget_string: stats?.period_planned_budget
            ? this.i18n.formatPrice(stats.period_planned_budget)
            : this.i18n.formatPrice(0),
          critical_assets_invest_next_year_budget_string: stats?.critical_assets_invest_next_year_budget
            ? this.i18n.formatPrice(stats.critical_assets_invest_next_year_budget)
            : this.i18n.formatPrice(0),
          dead_assets_hypothesis_invest_budget_string: stats?.dead_assets_hypothesis_invest_budget
            ? this.i18n.formatPrice(stats.dead_assets_hypothesis_invest_budget)
            : this.i18n.formatPrice(0),
          dead_assets_planned_invest_budget_string: stats?.dead_assets_planned_invest_budget
            ? this.i18n.formatPrice(stats.dead_assets_planned_invest_budget)
            : this.i18n.formatPrice(0),
        };

        if (this.perimeterStats.impact_indicators) {
          this.perimeterStats.impact_indicators.map((indicator) => {
            indicator.budget_string = this.i18n.formatPrice(indicator.total_budget || 0);
          })
        }

        this.cdRef.detectChanges();
      }),
    );
    const tabsConfig = this.configService.getSettingValue('ENERGY__TABS', []);
    this.showInitiatives = tabsConfig?.includes('ACTION_TABLE');
    this.showTrajectories = tabsConfig?.includes('TRAJECTORIES');
    this.showInitiativesSubject$.next(this.showInitiatives);
    this.showTrajectoriesSubject$.next(this.showTrajectories);

    this.initializeBackdropClick();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public onChangeTab(event: NbTabComponent): void {
    this.selectedTabId = Number(event.tabId);
  }

  public changeView(view: SiteOverviewView) {
    if (view === 'obligations' && this.selectedView !== 'obligations') {
      this.canAccessObligations$.pipe(take(1)).subscribe((canAccessObligations) => {
        if (canAccessObligations) {
          this.selectedView = view;
          this.cdRef.markForCheck();
        }
      });
      return;
    }
    this.selectedView = view;
  }

  public close(): void {
    this.nbDialogRef.close();
    this.layoutService.resetSheetDialogNb();
  }

  public totalAssetChanged(totalAssets) {
    this.techStateAssets = totalAssets;
  }

  public getTotalAlerts(): number {
    if (!this.perimeter) {
      return 0;
    }
    
    if (this.alertsData) {
      const maintenanceAlerts = this.alertsData.maintenance_alerts || 0;
      const conformityAlerts = this.alertsData.compliance_alerts || 0;
      return maintenanceAlerts + conformityAlerts;
    }
    
    this.fetchAlertsData();
    return 0;
  }

  private fetchAlertsData(): void {
    if (!this.perimeter) return;
    const buildings = PerimetersService.getBuildingsIds([this.perimeter]);
    this.yearsService.latestYears$.pipe(
      take(1),
      switchMap(([startYear, endYear]) => {
        return this.spreadsheetSitesService.getSitesData(buildings, [startYear, endYear]);
      })
    ).subscribe((sitesData) => {
      const currentSiteData = sitesData.find(site => site.id === this.perimeter.id);
      if (currentSiteData) {
        this.alertsData = currentSiteData;
        this.cdRef.detectChanges(); 
      }
    });
  }

  public openEnergySettingsDialog(defaultSelectedTab: EnergySettingsType = 'energyObjectives'): void {
    this.dialogService.open(EnergySettingsDialogComponent, {
      autoFocus: false,
      context: {
        defaultSelectedTab: defaultSelectedTab,
      }
    });
  }

  public goToAudit() {
    const popupUrl = `${environment.auditAppURL}/perimeters?site=${this.perimeter.id}`
    this.utils.openPopupWindow(popupUrl)
  }

  public openEditSite() {
    this.rightPanelDialogService.open(
      SitesSideFormComponent,
      {
        autoFocus: false,
        closeOnBackdropClick: false,
        context: { siteId: this.perimeter.id }
      }
    ).onClose.pipe(
      filter((result): result is number | {
        siteId: number;
        focusedPerimeterId?: number;
        focusedPerimeter?: KPI.Perimeter;
        deletedPerimeterId?: number;
      } => result !== null && result !== undefined),
      map((result) => typeof result === 'number'
        ? { siteId: result, focusedPerimeterId: null, focusedPerimeter: null, deletedPerimeterId: null }
        : result
      ),
      switchMap(({ siteId, focusedPerimeterId, focusedPerimeter, deletedPerimeterId }) => {
        if (siteId === this.perimeter.id) {
          this.referenceDataLoading = true;
          this.cdRef.markForCheck();

          return combineLatest([
            this.api.structures_perimeter(siteId),
            this.api.audit_perimeters(),
          ]).pipe(
            map(([updatedPerimeter, allPerimeters]) => {
              const fullPerimeter = ScopeService.mapPerimetersFilterRefData(allPerimeters)
                .find((perimeter) => perimeter.id === siteId);
              return {
                updatedPerimeter: {
                  ...updatedPerimeter,
                  sub_perimeters: fullPerimeter?.sub_perimeters || this.perimeter.sub_perimeters || [],
                },
                focusedPerimeterId,
                focusedPerimeter,
                deletedPerimeterId,
              };
            }),
            retry({
              count: 3,
              delay: 1000,
              resetOnSuccess: true
            }),
            catchError((error) => {
              this.loadingPicture = false;
              return this.configService.customerConfig$.pipe(
                take(1),
                tap((customerConfig) => {
                  if (customerConfig?.picture) {
                    this.perimeterPicture = customerConfig.picture.picture;
                  } else {
                    this.perimeterPicture = 'assets/images/site-default.jpg';
                  }
                  this.cdRef.detectChanges();
                }),
                switchMap(() => throwError(() => error))
              );
            })
          );
        } else {
          this.perimetersService.perimetersHaveChanged();
          this.cdRef.detectChanges();
          return of(null);
        }
      }),
      finalize(() => {
        this.referenceDataLoading = false;
        this.cdRef.markForCheck();
      }),
      filter((result) => result !== null),
      tap(({
        updatedPerimeter,
        focusedPerimeterId,
        focusedPerimeter: createdFocusedPerimeter,
        deletedPerimeterId,
      }: {
        updatedPerimeter: KPI.Perimeter;
        focusedPerimeterId: number | null;
        focusedPerimeter: KPI.Perimeter | null;
        deletedPerimeterId: number | null;
      }) => {
        const subPerimetersById = new Map<number, KPI.Perimeter>();
        (this.perimeter.sub_perimeters || []).forEach((perimeter) => {
          subPerimetersById.set(perimeter.id, perimeter);
        });
        (updatedPerimeter.sub_perimeters || []).forEach((perimeter) => {
          subPerimetersById.set(perimeter.id, perimeter);
        });
        if (createdFocusedPerimeter) {
          subPerimetersById.set(createdFocusedPerimeter.id, createdFocusedPerimeter);
        }
        if (deletedPerimeterId) {
          subPerimetersById.delete(deletedPerimeterId);
          Array.from(subPerimetersById.values())
            .filter((perimeter) => perimeter.level_parent === deletedPerimeterId)
            .forEach((perimeter) => subPerimetersById.delete(perimeter.id));
        }
        updatedPerimeter = {
          ...updatedPerimeter,
          sub_perimeters: Array.from(subPerimetersById.values()),
        };
        this.perimeter = updatedPerimeter;
        this.updateSelectedPerimeterIfNeeded(updatedPerimeter);
        const focusedPerimeter = updatedPerimeter.sub_perimeters?.find((perimeter) =>
          perimeter.id === focusedPerimeterId
        ) || updatedPerimeter;
        this.focusedPerimeterId = focusedPerimeter.id;
        this.referenceDataRefreshKey += 1;
        this.perimetersService.setSitePerimeterFocused(updatedPerimeter.id, focusedPerimeter);
        this.focusedPerimeter$ = this.perimetersService.getSitePerimeterFocused(this.perimeter);
        this.loadingPicture = true;
        const perimeterPictures = updatedPerimeter.pictures || [];
        if (perimeterPictures.length > 0) {
          if (this.perimeter.cover_image) {
            const perimeterCoverImage = perimeterPictures.find((p) => p.id === this.perimeter.cover_image);
            if (perimeterCoverImage) {
              this.perimeterPicture = perimeterCoverImage.picture;
            } else {
              this.perimeterPicture = perimeterPictures[0].picture;
            }
          } else {
            this.perimeterPicture = perimeterPictures[0].picture;
          }
          this.loadingPicture = false;
          this.cdRef.detectChanges();
        } else {
          this.subscriptions.push(
            this.configService.customerConfig$.pipe(take(1)).subscribe((customerConfig) => {
              if (customerConfig?.picture) {
                this.perimeterPicture = customerConfig.picture.picture;
              } else {
                this.perimeterPicture = 'assets/images/site-default.jpg';
              }
              this.loadingPicture = false;
              this.cdRef.detectChanges();
            })
          );
        }
      })
    ).subscribe();
  }

  private updateSelectedPerimeterIfNeeded(updatedPerimeter: KPI.Perimeter): void {
    this.subscriptions.push(
      this.perimetersService.perimeters$.pipe(take(1)).subscribe((selectedPerimeters) => {
        if (!selectedPerimeters?.some((perimeter) => perimeter.id === updatedPerimeter.id)) {
          return;
        }

        const nextSelectedPerimeters = selectedPerimeters.map((perimeter) =>
          perimeter.id === updatedPerimeter.id ? updatedPerimeter : perimeter
        );
        this.perimetersService.updateSelectedPerimeters(nextSelectedPerimeters);
        this.perimetersService.previousPerimeters$.next([]);
      })
    );
  }

  public initializeBackdropClick(): void {
    this.nbDialogRef.onBackdropClick.pipe(take(1)).subscribe(() => {
      this.confirmClose()
    });
  }

  protected confirmClose(): void {
    this.referenceDataService.anyValueChangedFromInitial$
      .pipe(take(1))
      .subscribe((hasChanged) => {
        if (hasChanged) {
          this.dialogService.open(
            SideFormDialogComponent,
            {
              autoFocus: false,
              closeOnBackdropClick: false,
              context: {
                wider: true,
                title: this.translate.instant('Unregistered changes'),
                content: this.translate.instant('Would you like to exit ?'),
              },
            }
          ).onClose.subscribe((confirmation: boolean) => {
            if (confirmation) {
              this.close();
            }
          });
        } else {
          this.close();
        }
      });
  }
}
